import type { TFunction } from 'i18next';
import { BonusShopProductType } from '@/api/cms/bonusShop';
import type { IBonusShopExchangeEvent } from '@/modules/googleAnalytics';
import type { BonusShopProduct, BonusShopProductVersion } from './types';

export enum BonusShopTab {
  ALL = 0,
  FREE_SPINS = 1,
  CASH_BONUS = 2,
  CASH = 3,
  LOOTBOX = 4,
  PREMIUM = 5,
}

const TAB_KEYS = [
  'all',
  'freeSpins',
  'cashBonus',
  'cash',
  'lootbox',
  'premium',
];

export const getTabs = (t: TFunction) =>
  TAB_KEYS.map((key) => t(`tabs.${key}`));

export const TAB_TO_TYPE: Record<BonusShopTab, BonusShopProductType | null> = {
  [BonusShopTab.ALL]: null,
  [BonusShopTab.FREE_SPINS]: BonusShopProductType.FREE_SPINS,
  [BonusShopTab.CASH_BONUS]: BonusShopProductType.CASH_BONUS,
  [BonusShopTab.CASH]: BonusShopProductType.CASH,
  [BonusShopTab.LOOTBOX]: BonusShopProductType.LOOTBOX,
  [BonusShopTab.PREMIUM]: BonusShopProductType.PREMIUM,
};

const SECTION_TABS = [
  BonusShopTab.FREE_SPINS,
  BonusShopTab.CASH_BONUS,
  BonusShopTab.CASH,
  BonusShopTab.LOOTBOX,
  BonusShopTab.PREMIUM,
];

const SECTION_TITLE_KEYS: Partial<Record<BonusShopTab, string>> = {
  [BonusShopTab.CASH]: 'sections.cash',
};

export const getSectionTitle = (t: TFunction, tab: BonusShopTab) =>
  t(SECTION_TITLE_KEYS[tab] || `tabs.${TAB_KEYS[tab]}`);

export const getSections = (t: TFunction) =>
  SECTION_TABS.map((tab) => ({
    tab,
    type: TAB_TO_TYPE[tab] as BonusShopProductType,
    title: getSectionTitle(t, tab),
  }));

const AMOUNT_EVENT_TYPES: BonusShopProductType[] = [
  BonusShopProductType.CASH,
  BonusShopProductType.CASH_BONUS,
];

export const getExchangeEventData = (
  product: BonusShopProduct,
  currency: string,
  version?: BonusShopProductVersion
): IBonusShopExchangeEvent => ({
  type: product.type,
  bonus_id: version ? version.group : product.group,
  cp_price: version ? version.cpPrice : product.cpPrice,
  currency,
  ...(version && { fs_amount: version.fsCount }),
  ...(AMOUNT_EVENT_TYPES.includes(product.type) &&
    product.amountValue != null && { amount: product.amountValue }),
});

export const formatCompPoints = (points: number) =>
  points.toLocaleString('en-US');

const ERROR_MAX_LENGTH = 100;

const toText = (value: unknown): string => {
  if (Array.isArray(value)) return value.map(toText).filter(Boolean).join(', ');
  if (typeof value === 'string') return value;
  if (value instanceof Error) return value.message;

  return value == null ? '' : JSON.stringify(value);
};

export const formatExchangeError = (error?: unknown) => {
  if (!error) return '';

  const isFieldMap =
    typeof error === 'object' &&
    !Array.isArray(error) &&
    !(error instanceof Error);

  const text = isFieldMap
    ? Object.entries(error as Record<string, unknown>)
        .map(([field, value]) => `${field}: ${toText(value)}`)
        .join('; ')
    : toText(error);

  return text.slice(0, ERROR_MAX_LENGTH);
};
